import React, { useCallback, useMemo, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useMeCore } from "../../../../cores/me";
import { useSocialPostCore } from "../../../../cores/socialPost";
import {
  SocialProviderId,
  SocialProviderRules,
  socialProviderIdsMatch,
} from "../../../../cores/socialPost/config";
import { PostErrorType } from "../../../../cores/socialPost/interfaces/postError";
import { SocialProvider } from "../../../../cores/socialPost/interfaces/socialProvider";
import { SocialType } from "../../../../cores/socialPost/interfaces/postError";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faChevronDown, faTh, faPlay, faCheck } from "@fortawesome/free-solid-svg-icons";
import { PostType } from "../../../../cores/socialPost/enums/postType";
import { FacebookIcon } from "../../social/Facebook/FacebookIcon";
import { InstagramIcon } from "../../social/Instagram/InstagramIcon";
import { TwitterIcon } from "../../social/Twitter/TwitterIcon";
import { LinkedInIcon } from "../../social/LinkedIn/LinkedInIcon";
import { TiktokIcon } from "../../social/Tiktok/TiktokIcon";
import { ConnectSocialModal } from "./ConnectSocialModal";
import "./ModernSocialPostSelector.scss";

interface Props {
  socialType?: SocialType;
  hideTitle?: boolean;
}

export const ModernSocialPostSelector = ({
  socialType = SocialType.Post,
  hideTitle = false,
}: Props) => {
  const intl = useIntl();
  const { socialSettings } = useMeCore();
  const {
    socialProviders,
    postErrors,
    images,
    video,
    selectedPost,
    postType,
    setPostTypeAction,
    addSocialProvider,
    removeSocialProvider,
    clearSocialProviders,
  } = useSocialPostCore();

  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showInstagramDropdown, setShowInstagramDropdown] = useState(false);


  const hasError = useCallback(
    (id: string, errorType: PostErrorType) =>
      (postErrors as any[]).some(
        ({ type, socialProviderId }: any) =>
          socialProviderIdsMatch(socialProviderId, id) && type === errorType
      ),
    [postErrors]
  );

  const isSelected = useCallback(
    (id: string) => (socialProviders as any[]).some((sp: any) => sp.id === id),
    [socialProviders]
  );

  const socialProvidersAbleToPost: SocialProvider[] = useMemo(() => {
    const providers: SocialProvider[] = [];
    
    if ((socialSettings as any)?.facebook) {
      providers.push({
        id: SocialProviderId.Facebook,
        name: SocialProviderId.Facebook,
        displayName: "Facebook",
        icon: "facebook",
        color: "#1877F2",
      });
    }
    
    if ((socialSettings as any)?.instagram) {
      providers.push({
        id: SocialProviderId.Instagram,
        name: SocialProviderId.Instagram,
        displayName: "Instagram",
        icon: "instagram",
        color: "#E4405F",
      });
    }
    
    if ((socialSettings as any)?.twitter) {
      providers.push({
        id: SocialProviderId.Twitter,
        name: SocialProviderId.Twitter,
        displayName: "Twitter",
        icon: "twitter",
        color: "#1DA1F2",
      });
    }
    
    if ((socialSettings as any)?.linkedIn) {
      providers.push({
        id: SocialProviderId.LinkedIn,
        name: SocialProviderId.LinkedIn,
        displayName: "LinkedIn",
        icon: "linkedIn",
        color: "#0077B5",
      });
    }
    
    if ((socialSettings as any)?.linkedInBusiness) {
      providers.push({
        id: SocialProviderId.LinkedInBusiness,
        name: SocialProviderId.LinkedInBusiness,
        displayName: "LinkedIn Business",
        icon: "linkedInBusiness",
        color: "#0077B5",
      });
    }
    
    if ((socialSettings as any)?.tiktok) {
      providers.push({
        id: SocialProviderId.TikTok,
        name: SocialProviderId.TikTok,
        displayName: "TikTok",
        icon: "tiktok",
        color: "#000000",
      });
    }

    return providers;
  }, [socialSettings]);

  // Al editar desde calendario / historial: una sola red como el SocialPostSelector clásico
  useEffect(() => {
    if (!selectedPost?.post?.socialProvider) return;
    const rawId = selectedPost.post.socialProvider;
    const provider = socialProvidersAbleToPost.find((p) =>
      socialProviderIdsMatch(p.id, rawId)
    );
    if (!provider) return;
    clearSocialProviders();
    addSocialProvider(provider);
  }, [
    selectedPost,
    socialProvidersAbleToPost,
    clearSocialProviders,
    addSocialProvider,
  ]);

  const getProviderIcon = (provider: SocialProvider) => {
    const iconProps = {
      size: "1x" as const,
      color: isSelected(provider.id) ? "#ffffff" : provider.color,
    };

    switch (provider.id) {
      case SocialProviderId.Facebook:
        return <FacebookIcon {...iconProps} />;
      case SocialProviderId.Instagram:
        return <InstagramIcon {...iconProps} />;
      case SocialProviderId.Twitter:
        return <TwitterIcon {...iconProps} />;
      case SocialProviderId.LinkedIn:
        return <LinkedInIcon {...iconProps} />;
      case SocialProviderId.LinkedInBusiness:
        return <LinkedInIcon {...iconProps} />;
      case SocialProviderId.TikTok:
        return <TiktokIcon {...iconProps} />;
      default:
        return null;
    }
  };

  const handleProviderClick = (provider: SocialProvider) => {
    if (!selectedPost) {
      if (isSelected(provider.id)) {
        removeSocialProvider(provider);
      } else {
        addSocialProvider(provider);
      }
    }
  };

  const handleConnectSocial = (providerId: string) => {
    // El modal de conexión maneja la autenticación directamente
    // Solo cerramos el modal después de la selección
    setShowConnectModal(false);
  };

  const handleInstagramClick = (provider: SocialProvider) => {
    // Instagram se puede seleccionar/deseleccionar normalmente
    handleProviderClick(provider);
  };

  const handlePostTypeSelect = (postType: PostType) => {
    setPostTypeAction(postType);
    setShowInstagramDropdown(false);
  };

  const getPostTypeDisplay = () => {
    return postType === PostType.POST ? 'Post' : 'Reel';
  };

  // Cerrar dropdown cuando Instagram se deselecciona
  useEffect(() => {
    const isInstagramSelected = isSelected(SocialProviderId.Instagram);
    if (!isInstagramSelected) {
      setShowInstagramDropdown(false);
    }
  }, [socialProviders, isSelected]);

  return (
    <div className="modern-social-selector">
      {!hideTitle && (
        <div className="selector-header">
          <h4 className="selector-title">
            {intl.formatMessage({ id: "app.socialPostSelector.createTitle" })}
          </h4>
        </div>
      )}
      <div className="providers-container">
        {socialProvidersAbleToPost.map((provider) => {
          const selected = isSelected(provider.id);
          const hasAnyError = (postErrors as any[]).some(
            ({ socialProviderId }: any) =>
              socialProviderIdsMatch(socialProviderId, provider.id)
          );

          // Instagram con botón de tipo separado
          if (provider.id === SocialProviderId.Instagram) {
            return (
              <React.Fragment key={provider.id}>
                {/* Botón de Instagram */}
                <button
                  className={`provider-button ${selected ? 'selected' : ''} ${hasAnyError ? 'error' : ''}`}
                  onClick={() => handleInstagramClick(provider)}
                  disabled={!!selectedPost}
                  style={{
                    '--provider-color': provider.color,
                  } as React.CSSProperties}
                >
                  <div className="provider-icon">
                    {getProviderIcon(provider)}
                  </div>
                  <span className="provider-name">{provider.displayName}</span>
                </button>

                {/* Botón de tipo de post para Instagram */}
                {selected && (
                  <div className="instagram-post-type-container">
                    <button
                      className="post-type-button"
                      onClick={() => setShowInstagramDropdown(!showInstagramDropdown)}
                      disabled={!!selectedPost}
                    >
                      <span className="post-type-text">{getPostTypeDisplay()}</span>
                      <FontAwesomeIcon 
                        icon={faChevronDown} 
                        className={`chevron ${showInstagramDropdown ? 'rotated' : ''}`}
                      />
                    </button>

                    {/* Dropdown para tipos de post */}
                    {showInstagramDropdown && (
                      <div className="instagram-post-type-dropdown">
                        <div
                          className={`dropdown-item ${postType === PostType.POST ? 'selected' : ''}`}
                          onClick={() => handlePostTypeSelect(PostType.POST)}
                        >
                          <div className="item-left">
                            <FontAwesomeIcon icon={faTh} className="item-icon" />
                            <div className="item-content">
                              <div className="item-title">Post</div>
                              <div className="item-description">{intl.formatMessage({ id: "app.socialPostSelector.postDescription" })}</div>
                            </div>
                          </div>
                          {postType === PostType.POST && (
                            <FontAwesomeIcon icon={faCheck} className="check-icon" />
                          )}
                        </div>
                        
                        <div
                          className={`dropdown-item ${postType === PostType.REEL ? 'selected' : ''}`}
                          onClick={() => handlePostTypeSelect(PostType.REEL)}
                        >
                          <div className="item-left">
                            <FontAwesomeIcon icon={faPlay} className="item-icon" />
                            <div className="item-content">
                              <div className="item-title">Reel</div>
                              <div className="item-description">{intl.formatMessage({ id: "app.socialPostSelector.reelDescription" })}</div>
                            </div>
                          </div>
                          {postType === PostType.REEL && (
                            <FontAwesomeIcon icon={faCheck} className="check-icon" />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            );
          }

          // Otros proveedores (Facebook, TikTok, etc.)
          return (
            <button
              key={provider.id}
              className={`provider-button ${selected ? 'selected' : ''} ${hasAnyError ? 'error' : ''}`}
              onClick={() => handleProviderClick(provider)}
              disabled={!!selectedPost}
              style={{
                '--provider-color': provider.color,
              } as React.CSSProperties}
            >
              <div className="provider-icon">
                {getProviderIcon(provider)}
              </div>
              <span className="provider-name">{provider.displayName}</span>
            </button>
          );
        })}
        
        {/* Botón para conectar más redes sociales */}
        <button
          className="provider-button add-provider-button"
          onClick={() => setShowConnectModal(true)}
          disabled={!!selectedPost}
        >
          <div className="provider-icon">
            <FontAwesomeIcon icon={faPlus} />
          </div>
          <span className="provider-name">{intl.formatMessage({ id: "app.socialPostSelector.connectMore" })}</span>
        </button>
      </div>
      
      {/* Modal para conectar redes sociales */}
      <ConnectSocialModal
        isOpen={showConnectModal}
        toggle={() => setShowConnectModal(false)}
        onConnect={handleConnectSocial}
      />
    </div>
  );
};
